import React, { useEffect, useState } from 'react';
import * as emotion from 'emotion';
import * as emotionCore from '@emotion/core';
import * as emotionTheming from 'emotion-theming';
import { useHasBeenSeen } from '@root/src/web/lib/useHasBeenSeen';
import { getWeeklyArticleHistory, logView, } from '@root/node_modules/@guardian/automat-client';
import { shouldHideSupportMessaging, getArticleCountConsent, } from '@root/src/web/lib/contributions';
import { getCookie } from '@root/src/web/browser/cookie';
import { sendOphanComponentEvent, submitComponentEvent, } from '@root/src/web/browser/ophan/ophan';
import { getZIndex } from '@root/src/web/lib/getZIndex';
import { trackNonClickInteraction } from '@root/src/web/browser/ga/ga';
import { getForcedVariant } from '@root/src/web/lib/readerRevenueDevUtils';
const checkForErrors = (response) => {
    if (!response.ok) {
        throw Error(response.statusText ||
            `SlotBanner | An api call returned HTTP status ${response.status}`);
    }
    return response;
};
// TODO specify return type (need to update client to provide this first)
const buildPayload = (props) => {
    return {
        tracking: {
            ophanPageId: window.guardian.config.ophan.pageViewId,
            platformId: 'GUARDIAN_WEB',
            clientName: 'dcr',
            referrerUrl: window.location.origin + window.location.pathname,
        },
        targeting: {
            alreadyVisitedCount: props.alreadyVisitedCount,
            shouldHideReaderRevenue: props.shouldHideReaderRevenue,
            isPaidContent: props.isPaidContent,
            showSupportMessaging: !shouldHideSupportMessaging(props.isSignedIn),
            engagementBannerLastClosedAt: props.engagementBannerLastClosedAt,
            subscriptionBannerLastClosedAt: props.subscriptionBannerLastClosedAt,
            mvtId: Number(getCookie('GU_mvt_id')),
            countryCode: props.countryCode,
            switches: props.switches,
            weeklyArticleHistory: getWeeklyArticleHistory(),
            hasOptedOutOfArticleCount: !props.hasConsentedToArticleCounts,
        },
    };
};
// TODO replace this with an imported version from the client lib
const getBanner = (meta, url) => {
    const json = JSON.stringify(meta);
    return fetch(url, {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: json,
    });
};
export const canShow = async ({ remoteBannerConfig, isSignedIn, asyncCountryCode, contentType, sectionName, shouldHideReaderRevenue, isMinuteArticle, isPaidContent, isSensitive, tags, contributionsServiceUrl, alreadyVisitedCount, engagementBannerLastClosedAt, subscriptionBannerLastClosedAt, switches, }) => {
    if (!remoteBannerConfig)
        return Promise.resolve({ result: false });
    if (shouldHideReaderRevenue || isPaidContent) {
        // We never serve Reader Revenue banners in this case
        return Promise.resolve({ result: false });
    }
    const countryCode = await asyncCountryCode;
    const hasConsentedToArticleCounts = await getArticleCountConsent();
    const bannerPayload = buildPayload({
        isSignedIn,
        countryCode,
        contentType,
        sectionName,
        shouldHideReaderRevenue,
        isMinuteArticle,
        isPaidContent,
        tags,
        contributionsServiceUrl,
        isSensitive,
        alreadyVisitedCount,
        engagementBannerLastClosedAt,
        subscriptionBannerLastClosedAt,
        switches,
        hasConsentedToArticleCounts,
    });
    const forcedVariant = getForcedVariant('banner');
    const queryString = forcedVariant ? `?force=${forcedVariant}` : '';
    return getBanner(bannerPayload, `${contributionsServiceUrl}/banner${queryString}`)
        .then(checkForErrors)
        .then((response) => response.json())
        .then((json) => {
        if (!json.data) {
            return { result: false };
        }
        const { module, meta } = json.data;
        return { result: true, meta: { module, meta } };
    });
};
export const ReaderRevenueBanner = ({ meta, module }) => {
    const [Banner, setBanner] = useState();
    const [hasBeenSeen, setNode] = useHasBeenSeen({
        threshold: 0,
        debounce: true,
    });
    useEffect(() => {
        if (module === undefined || meta === undefined) {
            return;
        }
        window.guardian.automat = {
            react: React,
            preact: React,
            emotionCore,
            emotionTheming,
            emotion,
        };
        window
            .guardianPolyfilledImport(module.url)
            .then((bannerModule) => {
            setBanner(() => bannerModule[module.name]); // useState requires functions to be wrapped
            sendOphanComponentEvent('INSERT', meta);
        })
            .catch((error) => 
        // eslint-disable-next-line no-console
        console.log(`banner - error is: ${error}`));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Should only run once
    useEffect(() => {
        if (hasBeenSeen && meta) {
            const { abTestName, componentType } = meta;
            logView(abTestName);
            sendOphanComponentEvent('VIEW', meta);
            // track banner view event in Google Analytics for subscriptions banner
            if (componentType === 'ACQUISITIONS_SUBSCRIPTIONS_BANNER') {
                trackNonClickInteraction('subscription-banner : display');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasBeenSeen]);
    if (Banner) {
        return (
        // The css here is necessary to put the container div in view, so that we can track the view
        React.createElement("div", { ref: setNode, className: emotion.css `width: 100%; ${getZIndex('banner')}` },
            React.createElement(Banner, Object.assign({}, module.props, { submitComponentEvent: submitComponentEvent }))));
    }
    return null;
};
